// src/components/DoorConfigurator.js

import React from 'react';
import bifoldImage from '../images/doorTypes/bifold.png';
import multislideImage from '../images/doorTypes/multislide.png';

// Swatch Images
import pureWhiteSwatch from '../images/swatches/pure-white.png';
import whiteWoodSwatch from '../images/swatches/white-wood.png';
import lightMatteGraySwatch from '../images/swatches/light-matte-gray.png';
import matteGraySwatch from '../images/swatches/matte-gray.png';
import metalGraySwatch from '../images/swatches/metal-gray.png';
import matteBlackSwatch from '../images/swatches/matte-black.png';
import champagneSwatch from '../images/swatches/champagne.png';
import goldenWoodSwatch from '../images/swatches/golden-wood.png';
import yellowWoodSwatch from '../images/swatches/yellow-wood.png';
import redWoodSwatch from '../images/swatches/red-wood.png';
import brownWoodSwatch from '../images/swatches/brown-wood.png';

// Bi-Fold Slide Direction Images
import BiFold_Inside_OpenLeft from '../images/biFold/Inside/BiFold_Inside_OpenLeft.png';
import BiFold_Inside_OpenMiddle from '../images/biFold/Inside/BiFold_Inside_OpenMiddle.png';
import BiFold_Inside_OpenRight from '../images/biFold/Inside/BiFold_Inside_OpenRight.png';

import BiFold_Outside_OpenLeft from '../images/biFold/Outside/BiFold_Outside_OpenLeft.png';
import BiFold_Outside_OpenMiddle from '../images/biFold/Outside/BiFold_Outside_OpenMiddle.png';
import BiFold_Outside_OpenRight from '../images/biFold/Outside/BiFold_Outside_OpenRight.png';

// Multi-Slide Slide Direction Images
import MultiSlide_OpenLeft from '../images/multiSlide/MultiSlide_OpenLeft.png';
import MultiSlide_OpenMiddle from '../images/multiSlide/MultiSlide_OpenMiddle.png';
import MultiSlide_OpenRight from '../images/multiSlide/MultiSlide_OpenRight.png';

function DoorConfigurator({
	door,
	doorId, // Use doorId instead of doorIndex
	selectDoorOption,
	removeDoor,
	params,
	isQuoteBuilder = false, // New prop to control visibility of additional options
	hasAttemptedNext = false, // New prop for validation state
}) {
	let doorIndex = params.doors.findIndex(d => d.id === doorId);

	const displayDoorIndex = doorIndex !== -1 ? doorIndex + 1 : "Unknown";
	// Define color options to avoid repetition
	const colorOptions = [
		'Pure White',
		'White Wood',
		'Light Matte Gray',
		'Matte Gray',
		'Metal Gray',
		'Matte Black',
		'Champagne',
		'Golden Wood',
		'Yellow Wood',
		'Red Wood',
		'Brown Wood',
	];

	// Swatch Images Mapping
	const swatchImages = {
		'Pure White': pureWhiteSwatch,
		'White Wood': whiteWoodSwatch,
		'Light Matte Gray': lightMatteGraySwatch,
		'Matte Gray': matteGraySwatch,
		'Metal Gray': metalGraySwatch,
		'Matte Black': matteBlackSwatch,
		'Champagne': champagneSwatch,
		'Golden Wood': goldenWoodSwatch,
		'Yellow Wood': yellowWoodSwatch,
		'Red Wood': redWoodSwatch,
		'Brown Wood': brownWoodSwatch,
	};

	// Determine slide directions for Bi-Fold based on openingDirection
	const getSlideDirectionsBiFold = () => {
		if (door.openingDirection === 'Accordion Outside') {
			return [
				{ label: 'Left', image: BiFold_Outside_OpenLeft },
				{ label: 'Middle', image: BiFold_Outside_OpenMiddle },
				{ label: 'Right', image: BiFold_Outside_OpenRight },
			];
		}
		// Default to 'Accordion Inside' if not 'Accordion Outside'
		return [
			{ label: 'Left', image: BiFold_Inside_OpenLeft },
			{ label: 'Middle', image: BiFold_Inside_OpenMiddle },
			{ label: 'Right', image: BiFold_Inside_OpenRight },
		];
	};

	// Slide Directions Definitions
	const slideDirectionsBiFold = getSlideDirectionsBiFold();

	const slideDirectionsMultiSlide = [
		{ label: 'Left', image: MultiSlide_OpenLeft },
		{ label: 'Middle', image: MultiSlide_OpenMiddle },
		{ label: 'Right', image: MultiSlide_OpenRight },
	];

	// Determine which slide directions to use based on door type
	let slideDirections = [];
	if (door.doorType === 'Bi-Fold') {
		slideDirections = slideDirectionsBiFold;
	} else if (door.doorType === 'Multi-Slide') {
		slideDirections = slideDirectionsMultiSlide;
	}

	// Determine if Dimensions are required (i.e., if width or height are not set)
	const isDimensionsRequired =
		(Number(door.widthFt || 0) + Number(door.widthIn || 0) === 0) ||
		(Number(door.heightFt || 0) + Number(door.heightIn || 0) === 0);


	return (

		<div className="bg-white p-5 rounded-[10px]  mb-4 relative" id={`door-${doorId}`} >
			<div className="mb-[32px]">
				<div className='flex justify-between items-center border-b-[1px] w-[100%] border-b-black pb-[25px] pt-[12px]'>
					<h3 className="text-[24px] font-semibold text-[#1D1D1D] capitalize">Door {displayDoorIndex}</h3>
					{/* {params.doors.length > 0 && ( */}
					{doorIndex > 0 && (
						<button
							onClick={() => removeDoor(doorId)}
							className="px-[20px] py-[8px] hover:bg-red-50 bg-[#E4DEDA] rounded text-[12px] text-[#1D1D1D] font-medium"
						>
							Remove Door
						</button>
					)}
				</div>
			</div>

			{/* Door Type Section */}
			<div className="mb-8 flex flex-col md:flex-row md:items-start md:gap-8">
				<div className="flex items-center gap-2 md:block w-full md:w-[197px] pb-2">
					<label className="block text-[20px] font-medium">Door Type</label>
					Select One
					{/* {!door.doorType && (
						<div
							className={`text-[16px] font-normal md:mt-1 ${hasAttemptedNext ? 'text-red-500' : 'text-black'
								}`}
						>
							Select One
						</div>
					)} */}
				</div>
				<div className="flex-1">
					<div className="grid grid-cols-2 gap-4">
						{/* Bi-Fold Door Option */}
						<div className="flex flex-col items-center">
							<div
								onClick={() => selectDoorOption(doorId, 'doorType', 'Bi-Fold')}
								className="cursor-pointer w-full group"
							>
								<div className="aspect-square w-full rounded-lg overflow-hidden mb-2">
									<div
										className={`w-full h-full bg-gray-100 flex items-center justify-center transition-opacity duration-200 ${!door.doorType
											? 'opacity-100 group-hover:opacity-100'
											: door.doorType !== 'Bi-Fold'
												? 'opacity-60 group-hover:opacity-80'
												: ''
											}`}
									>
										<img
											src={bifoldImage}
											alt="Bi-Fold Door"
											className="object-cover w-full h-full"
										/>
									</div>
								</div>
								<button
									className={`${door.doorType === 'Bi-Fold'
										? 'bg-[#699357] text-white'
										: 'bg-[#F5F1ED] text-[#1D1D1D]'
										} px-[24px] py-[12px] rounded w-full border-0 transition-colors text-[12px] md:text-[16px] leading-[20px] font-faktum font-medium`}
								>
									Bi-Fold
								</button>
							</div>
						</div>
						{/* Multi-Slide Door Option */}
						<div className="flex flex-col items-center">
							<div
								onClick={() => selectDoorOption(doorId, 'doorType', 'Multi-Slide')}
								className="cursor-pointer w-full group"
							>
								<div className="aspect-square w-full rounded-lg overflow-hidden mb-2 text-[16px]">
									<div
										className={`w-full h-full bg-gray-100 flex items-center justify-center transition-opacity duration-200 ${!door.doorType
											? 'opacity-100 group-hover:opacity-100'
											: door.doorType !== 'Multi-Slide'
												? 'opacity-60 group-hover:opacity-80'
												: ''
											}`}
									>
										<img
											src={multislideImage}
											alt="Multi-Slide Door"
											className="object-cover w-full h-full"
										/>
									</div>
								</div>
								<button
									className={`${door.doorType === 'Multi-Slide'
										? 'bg-[#699357] text-white'
										: 'bg-[#F5F1ED] text-[#1D1D1D]'
										} px-[24px] py-[12px] rounded w-full border-0 transition-colors text-[12px] md:text-[16px] leading-[20px] font-faktum font-medium`}
								>
									Multi-Slide
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Dimensions Section */}
			{door.doorType && (
				<div className="mb-8 flex flex-col md:flex-row md:items-start md:gap-8">
					<div className="flex items-center gap-2 md:block w-full md:w-[197px] pb-2">
						<label className="leading-[150%] text-[#1D1D1D] text-[20px] font-faktum font-medium block">Dimensions</label>
						{isDimensionsRequired && (
							<div
								className={`text-sm md:mt-1 ${hasAttemptedNext ? 'text-red-500' : 'text-gray-500'
									}`}
							>
								(Required)
							</div>
						)}
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-5 flex-1">
						<div>
							<label className="block mb-[8px] font-faktum font-normal text-[16px] leading-[150%] text-[#1D1D1D]">Width</label>
							<div className="flex flex-wrap max-w-[370px] gap-10 md:gap-2 justify-normal items-center">
								<div className='flex gap-x-2 items-center'>
									<input
										type="number"
										inputMode="numeric"
										min="0"
										max="999"
										value={door.widthFt}
										onFocus={(e) => {
											if (e.target.value === "0") e.target.value = ""; // Clear if value is "0"
										}}
										onBlur={(e) => {
											if (e.target.value === "") e.target.value = "0"; // Reset to "0" if left empty
										}}
										onChange={(e) => {
											let value = e.target.value.replace(/[^\d]/g, '');
											if (value.length > 3) {
												value = value.slice(0, 3);
											}
											selectDoorOption(doorId, 'widthFt', value);
										}}
										className="w-[107px] border-[#1D1D1D] h-[48px] py-[8px] pl-[8px] border rounded text-sm"
										placeholder="ft"
									/>
									<span className="font-faktum font-normal text-[16px] leading-[150%] text-[#1D1D1D]">ft</span>
								</div>
								<div className='flex gap-x-2 items-center'>
									<input
										type="number"
										inputMode="numeric"
										pattern="[0-9]*"
										min="0"
										max="9999"
										value={door.widthIn}
										onFocus={(e) => {
											if (e.target.value === "0") e.target.value = ""; // Clear if value is "0"
										}}
										onBlur={(e) => {
											if (e.target.value === "") e.target.value = "0"; // Reset to "0" if left empty
										}}
										onChange={(e) => {
											let value = e.target.value.replace(/[^\d]/g, '');
											if (value.length > 3) {
												value = value.slice(0, 3);
											}
											selectDoorOption(doorId, 'widthIn', value);
										}}
										className="w-[107px] border-[#1D1D1D] h-[48px] py-[8px] pl-[8px] border rounded text-sm"
										placeholder="in"
									/>
									<span className="font-faktum font-normal text-[16px] leading-[150%] text-[#1D1D1D]">in</span>
								</div>
							</div>

						</div>

						<div>
							<label className="block mb-[8px] font-faktum font-normal text-[16px] leading-[150%] text-[#1D1D1D]">Height</label>
							<div className="flex flex-wrap max-w-[370px] gap-10 md:gap-2 justify-normal items-center">
								<div className='flex gap-x-2 items-center'>
									<input
										type="number"
										inputMode="numeric"
										min="0"
										value={door.heightFt}
										onFocus={(e) => {
											if (e.target.value === "0") e.target.value = ""; // Clear if value is "0"
										}}
										onBlur={(e) => {
											if (e.target.value === "") e.target.value = "0"; // Reset to "0" if left empty
										}}
										onChange={(e) => {
											let value = e.target.value.replace(/[^\d]/g, '');
											if (value.length > 3) {
												value = value.slice(0, 3);
											}
											selectDoorOption(doorId, 'heightFt', value);
										}}
										className="w-[107px] border-[#1D1D1D] h-[48px] py-[8px] pl-[8px] border rounded text-sm"
										placeholder="ft"
									/>
									<span className="font-faktum font-normal text-[16px] leading-[150%] text-[#1D1D1D]">ft</span>
								</div>
								<div className='flex gap-x-2 items-center'>
									<input
										type="number"
										inputMode="numeric"
										min="0"
										max="11"
										value={door.heightIn}
										onFocus={(e) => {
											if (e.target.value === "0") e.target.value = ""; // Clear if value is "0"
										}}
										onBlur={(e) => {
											if (e.target.value === "") e.target.value = "0"; // Reset to "0" if left empty
										}}
										onChange={(e) => {
											let value = e.target.value.replace(/[^\d]/g, '');
											if (value.length > 2) {
												value = value.slice(0, 2);
											}
											selectDoorOption(doorId, 'heightIn', value);
										}}
										className="w-[107px] border-[#1D1D1D] h-[48px] py-[8px] pl-[8px] border rounded text-sm"
										placeholder="in"
									/>
									<span className="font-faktum font-normal text-[16px] leading-[150%] text-[#1D1D1D]">in</span>
								</div>
							</div>

						</div>
					</div>
				</div>
			)}

			{/* Additional Options: Conditionally Rendered Based on isQuoteBuilder and Door Type */}
			{isQuoteBuilder && door.doorType && (
				<>
					{/* Opening Direction Section (Bi-Fold only) */}
					{door.doorType === 'Bi-Fold' && (
						<div className="mb-8 flex flex-col md:flex-row md:items-start md:gap-8">
							<label className="w-full md:w-[197px] text-[20px] font-medium mb-4 md:mb-0 md:pt-2">Open Direction</label>
							<div className="grid grid-cols-2 gap-2 flex-1">
								{/* Accordion Inside */}
								<div className="flex flex-col items-center">
									<div
										onClick={() =>
											selectDoorOption(doorId, 'openingDirection', 'Accordion Inside')
										}
										className="cursor-pointer w-full group"
									>
										<div className="aspect-square w-full rounded-lg overflow-hidden mb-2">
											<div
												className={`w-full h-full bg-gray-100 flex items-center justify-center transition-opacity duration-200 ${!door.openingDirection
													? 'opacity-60 group-hover:opacity-80'
													: door.openingDirection !== 'Accordion Inside'
														? 'opacity-60 group-hover:opacity-80'
														: ''
													}`}
											>
												<img
													src={BiFold_Inside_OpenLeft}
													alt="Accordion Inside"
													className="object-cover w-full h-full"
												/>
											</div>
										</div>
										<button
											className={`${door.openingDirection === 'Accordion Inside'
												? 'bg-[#699357] text-white'
												: 'bg-[#F5F1ED] text-[#1D1D1D]'
												} py-[12px] px-[24px] font-semibold rounded-[5px] w-full transition-colors text-[16px]`}
										>
											Accordion Inside
										</button>
									</div>
								</div>
								{/* Accordion Outside */}
								<div className="flex flex-col items-center">
									<div
										onClick={() =>
											selectDoorOption(doorId, 'openingDirection', 'Accordion Outside')
										}
										className="cursor-pointer w-full group"
									>
										<div className="aspect-square w-full rounded-lg overflow-hidden mb-2">
											<div
												className={`w-full h-full bg-gray-100 flex items-center justify-center transition-opacity duration-200 ${!door.openingDirection
													? 'opacity-60 group-hover:opacity-80'
													: door.openingDirection !== 'Accordion Outside'
														? 'opacity-60 group-hover:opacity-80'
														: ''
													}`}
											>
												<img
													src={BiFold_Outside_OpenLeft}
													alt="Accordion Outside"
													className="object-cover w-full h-full"
												/>
											</div>
										</div>
										<button
											className={`${door.openingDirection === 'Accordion Outside'
												? 'bg-[#699357] text-white'
												: 'bg-[#F5F1ED] text-[#1D1D1D]'
												} py-[12px] px-[24px] font-semibold rounded-[5px] w-full transition-colors text-[16px]`}
										>
											Accordion Outside
										</button>
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Slide Direction Section with Dynamic Images */}
					<div className="mb-8 flex flex-col md:flex-row md:items-start md:gap-8">
						<label className="w-full md:w-[197px] text-[20px] font-medium mb-4 md:mb-0 md:pt-2">Slide Direction</label>
						<div className="grid grid-cols-3 gap-2 flex-1">
							{slideDirections.map((direction) => (
								<div
									key={direction.label}
									onClick={() => selectDoorOption(doorId, 'slideDirection', direction.label)}
									className="cursor-pointer flex flex-col items-center group"
								>
									<div className="w-full aspect-square mb-[32px] overflow-hidden">
										<img
											src={direction.image}
											alt={`${direction.label} Slide Direction`}
											className={`w-full h-full object-cover transition-opacity duration-200 ${door.slideDirection === direction.label
												? 'opacity-100'
												: 'opacity-60 group-hover:opacity-80'
												}`}
										/>
									</div>
									<button
										className={`${door.slideDirection === direction.label
											? 'bg-[#699357] text-white hover:bg-[#547545]'
											: 'bg-[#F5F1ED] hover:bg-[#e1dcd7]'
											} text-[16px] font-semibold py-[12px] px-2 rounded-[5px] text-xs w-full`}
									>

										{direction.label}
									</button>
								</div>
							))}
						</div>
					</div>

					{/* Pocket Door Section (Multi-Slide only) */}
					{door.doorType === 'Multi-Slide' && door.slideDirection !== 'Middle' && (
						<div className="mb-8 flex flex-col md:flex-row md:items-start md:gap-8">
							<label
								className="relative group w-[265px] md:w-[197px] text-[20px] font-medium mb-4 md:mb-0 md:pt-2 flex items-center gap-2"
							>
								Pocket Door
								<span
								className="absolute hidden group-hover:flex flex-col items-center bg-black text-white text-sm rounded-md px-3 py-2  -top-[130px] md:-top-[120px] left-1/2 transform -translate-x-1/2 w-[295px] md:w-[300px] max-w-xs break-words z-10 shadow-lg"
							>
									<span className="absolute -bottom-[6px] left-1/2 md:left-[60%] transform -translate-x-1/2 w-4 h-4 bg-black rotate-45"></span>
									Pocket doors allow you to completely hide the glass in a recessed pocket hidden in your wall. Note: framing would be necessary to accommodate this in your space.
								</span>
								<svg xmlns="http://www.w3.org/2000/svg" className='cursor-pointer' width="22" height="22" viewBox="0 0 22 22" fill="none">
									<rect width="22" height="22" fill="#1E1E1E" />
									<path d="M-9494 -3020C-9494 -3108.37 -9422.37 -3180 -9334 -3180H6556C6644.37 -3180 6716 -3108.37 6716 -3020V6656C6716 6744.37 6644.37 6816 6556 6816H-9334C-9422.37 6816 -9494 6744.37 -9494 6656V-3020Z" fill="#404040" />
									<path d="M-9334 -3179H6556V-3181H-9334V-3179ZM6715 -3020V6656H6717V-3020H6715ZM6556 6815H-9334V6817H6556V6815ZM-9493 6656V-3020H-9495V6656H-9493ZM-9334 6815C-9421.81 6815 -9493 6743.81 -9493 6656H-9495C-9495 6744.92 -9422.92 6817 -9334 6817V6815ZM6715 6656C6715 6743.81 6643.81 6815 6556 6815V6817C6644.92 6817 6717 6744.92 6717 6656H6715ZM6556 -3179C6643.81 -3179 6715 -3107.81 6715 -3020H6717C6717 -3108.92 6644.92 -3181 6556 -3181V-3179ZM-9334 -3181C-9422.92 -3181 -9495 -3108.92 -9495 -3020H-9493C-9493 -3107.81 -9421.81 -3179 -9334 -3179V-3181Z" fill="white" fill-opacity="0.1" />
									<g clip-path="url(#clip0_11_2)">
										<rect width="1440" height="3220.89" transform="translate(-174 -1510)" fill="white" />
										<rect width="1440" height="2052" transform="translate(-174 -1248)" fill="#F5F1ED" />
										<rect x="-94" y="-1104" width="951" height="1750" rx="10" fill="white" />
										<circle cx="10.5615" cy="11" r="9.56152" fill="#699357" stroke="#699357" stroke-width="2" />
										<path d="M9.82994 9.79386L9.70194 5.40986H11.5419L11.4139 9.79386L11.0939 13.0259H10.1499L9.82994 9.79386ZM10.6459 16.6899C9.92594 16.6899 9.50994 16.2739 9.50994 15.5539C9.49394 14.8339 9.89394 14.4339 10.6459 14.4339C11.3979 14.4339 11.7819 14.8019 11.7979 15.5539C11.8139 16.2739 11.3979 16.6899 10.6459 16.6899Z" fill="white" />
									</g>
									<defs>
										<clipPath id="clip0_11_2">
											<rect width="1440" height="3220.89" fill="white" transform="translate(-174 -1510)" />
										</clipPath>
									</defs>
								</svg>
							</label>

							<div className="grid grid-cols-2 gap-2 flex-1">
								<div
									onClick={() => selectDoorOption(doorId, 'pocketDoor', 'Yes')}
									className={`text-[16px] font-semibold py-[12px] px-2 rounded-[5px] w-full cursor-pointer flex items-center justify-center text-center ${door.pocketDoor === 'Yes'
										? 'bg-[#699357] text-white hover:bg-[#547545]'
										: 'bg-[#F5F1ED] hover:bg-[#e1dcd7]'
										}`}
								>
									Yes
								</div>
								<div
									onClick={() => selectDoorOption(doorId, 'pocketDoor', 'No')}
									className={`text-[16px] font-semibold py-[12px] px-2 rounded-[5px] w-full cursor-pointer flex items-center justify-center text-center ${door.pocketDoor === 'No'
										? 'bg-[#699357] text-white hover:bg-[#547545]'
										: 'bg-[#F5F1ED] hover:bg-[#e1dcd7]'
										}`}
								>
									No
								</div>
							</div>
						</div>

					)}

					{/* Screen Section */}
					<div className="mb-8 flex flex-col md:flex-row md:items-start md:gap-8">
						<label
							className="relative group w-[250px] md:w-[197px] text-[20px] font-medium mb-4 md:mb-0 md:pt-2 flex items-center gap-2"
						>
							Screen
							<span
								className="absolute hidden group-hover:flex flex-col items-center bg-black text-white text-sm rounded-md px-3 py-2 -top-[90px] md:-top-[80px] left-1/2 transform -translate-x-1/2 w-[230px] md:w-[300px] max-w-xs break-words z-10 shadow-lg"
							>
								<span className="absolute -bottom-[6px] left-[30%] md:left-[43%] transform -translate-x-1/2 w-4 h-4 bg-black rotate-45"></span>
								Screens provided will be full-length, and may require an additional frame to assemble.
							</span>
							<svg xmlns="http://www.w3.org/2000/svg" className='cursor-pointer' width="22" height="22" viewBox="0 0 22 22" fill="none">
								<rect width="22" height="22" fill="#1E1E1E" />
								<path d="M-9494 -3020C-9494 -3108.37 -9422.37 -3180 -9334 -3180H6556C6644.37 -3180 6716 -3108.37 6716 -3020V6656C6716 6744.37 6644.37 6816 6556 6816H-9334C-9422.37 6816 -9494 6744.37 -9494 6656V-3020Z" fill="#404040" />
								<path d="M-9334 -3179H6556V-3181H-9334V-3179ZM6715 -3020V6656H6717V-3020H6715ZM6556 6815H-9334V6817H6556V6815ZM-9493 6656V-3020H-9495V6656H-9493ZM-9334 6815C-9421.81 6815 -9493 6743.81 -9493 6656H-9495C-9495 6744.92 -9422.92 6817 -9334 6817V6815ZM6715 6656C6715 6743.81 6643.81 6815 6556 6815V6817C6644.92 6817 6717 6744.92 6717 6656H6715ZM6556 -3179C6643.81 -3179 6715 -3107.81 6715 -3020H6717C6717 -3108.92 6644.92 -3181 6556 -3181V-3179ZM-9334 -3181C-9422.92 -3181 -9495 -3108.92 -9495 -3020H-9493C-9493 -3107.81 -9421.81 -3179 -9334 -3179V-3181Z" fill="white" fill-opacity="0.1" />
								<g clip-path="url(#clip0_11_2)">
									<rect width="1440" height="3220.89" transform="translate(-174 -1510)" fill="white" />
									<rect width="1440" height="2052" transform="translate(-174 -1248)" fill="#F5F1ED" />
									<rect x="-94" y="-1104" width="951" height="1750" rx="10" fill="white" />
									<circle cx="10.5615" cy="11" r="9.56152" fill="#699357" stroke="#699357" stroke-width="2" />
									<path d="M9.82994 9.79386L9.70194 5.40986H11.5419L11.4139 9.79386L11.0939 13.0259H10.1499L9.82994 9.79386ZM10.6459 16.6899C9.92594 16.6899 9.50994 16.2739 9.50994 15.5539C9.49394 14.8339 9.89394 14.4339 10.6459 14.4339C11.3979 14.4339 11.7819 14.8019 11.7979 15.5539C11.8139 16.2739 11.3979 16.6899 10.6459 16.6899Z" fill="white" />
								</g>
								<defs>
									<clipPath id="clip0_11_2">
										<rect width="1440" height="3220.89" fill="white" transform="translate(-174 -1510)" />
									</clipPath>
								</defs>
							</svg>
						</label>
						<div className="grid grid-cols-2 gap-2 flex-1">
							<div
								onClick={() => selectDoorOption(doorId, 'screen', 'Yes')}
								className={`text-[16px] font-semibold py-[12px] px-2 rounded-[5px] w-full cursor-pointer flex items-center justify-center text-center ${door.screen === 'Yes'
										? 'bg-[#699357] text-white hover:bg-[#547545]'
										: 'bg-[#F5F1ED] hover:bg-[#e1dcd7]'
									}`}
							>
								Yes
							</div>
							<div
								onClick={() => selectDoorOption(doorId, 'screen', 'No')}
								className={`text-[16px] font-semibold py-[12px] px-2 rounded-[5px] w-full cursor-pointer flex items-center justify-center text-center ${door.screen === 'No'
										? 'bg-[#699357] text-white hover:bg-[#547545]'
										: 'bg-[#F5F1ED] hover:bg-[#e1dcd7]'
									}`}
							>
								No
							</div>
						</div>
					</div>



					{/* Interior Color Section */}
					<div className="mb-8 flex flex-col md:flex-row md:items-start md:gap-8">
						<label className="w-full md:w-[197px] text-[20px] font-medium mb-4 md:mb-0 md:pt-2">Interior Color</label>
						<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-5 flex-1">
							{colorOptions.map((option) => (
								<div
									key={option}
									onClick={() => selectDoorOption(doorId, 'interiorColor', option)}
									className="cursor-pointer flex flex-col items-center group"
								>
									<div className="rounded-t-[5px] w-full h-16 overflow-hidden">
										<img
											src={swatchImages[option]} // Use the mapped swatch image
											alt={option}
											className="w-full h-full object-cover" // No opacity adjustments
										/>
									</div>
									<button
										className={`${door.interiorColor === option
											? 'bg-[#699357] text-white hover:bg-[#547545]'
											: 'bg-[#F5F1ED] hover:bg-[#e1dcd7]'
											} px-1 py-[12px] font-medium rounded-b-[5px] w-full`}
									>
										{option}
									</button>
								</div>
							))}
						</div>
					</div>

					{/* Exterior Color Same/Different Section */}
					<div className="mb-8 flex flex-col md:flex-row md:items-start md:gap-8">
						<label className="w-full md:w-[197px] text-[20px] font-medium mb-4 md:mb-0 md:pt-2">Exterior Color</label>
						<div className="grid grid-cols-2 gap-2 flex-1">
							<div
								onClick={() => selectDoorOption(doorId, 'exteriorColorSame', 'Yes')}
								className={`text-[16px] font-semibold py-[12px] px-2 rounded-[5px] w-full cursor-pointer flex items-center justify-center text-center ${door.exteriorColorSame === 'Yes'
									? 'bg-[#699357] text-white hover:bg-[#547545]'
									: 'bg-[#F5F1ED] hover:bg-[#e1dcd7]'
									}`}
							>
								Same as Interior
							</div>
							<div
								onClick={() => selectDoorOption(doorId, 'exteriorColorSame', 'No')}
								className={`text-[16px] font-semibold py-[12px] px-2 rounded-[5px] w-full cursor-pointer flex items-center justify-center text-center ${door.exteriorColorSame === 'No'
									? 'bg-[#699357] text-white hover:bg-[#547545]'
									: 'bg-[#F5F1ED] hover:bg-[#e1dcd7]'
									}`}
							>
								Different from Interior
							</div>
						</div>
					</div>

					{/* Exterior Color Selection if Different */}
					{door.exteriorColorSame === 'No' && (
						<div className="mb-8 flex flex-col md:flex-row md:items-start md:gap-8">
							<label className="w-full md:w-[197px] text-[20px] font-medium mb-4 md:mb-0 md:pt-2">
								Exterior Color
							</label>
							<div className="grid grid-cols-4 gap-4 flex-1">
								{colorOptions.map((option) => (
									<div
										key={option}
										onClick={() => selectDoorOption(doorId, 'exteriorColor', option)}
										className="cursor-pointer flex flex-col items-center group"
									>
										<div className="rounded-t-[5px] w-full h-16 overflow-hidden">
											<img
												src={swatchImages[option]} // Use the mapped swatch image
												alt={option}
												className="w-full h-full object-cover" // No opacity adjustments
											/>
										</div>
										<button
											className={`${door.exteriorColor === option
												? 'bg-[#699357] text-white hover:bg-[#547545]'
												: 'bg-[#F5F1ED] hover:bg-[#e1dcd7]'
												} px-2 py-[12px] font-medium rounded-b-[5px] text-xs w-full`}
										>
											{option}
										</button>
									</div>
								))}
							</div>
						</div>
					)}

					{/* Hardware Color Section */}
					<div className="flex flex-col md:flex-row md:items-start md:gap-8">
						<label className="w-full md:w-[197px] text-[20px] font-medium mb-4 md:mb-0 md:pt-2">Hardware Color</label>
						<div className="grid grid-cols-2 gap-2 flex-1">
							<div
								onClick={() => selectDoorOption(doorId, 'hardwareColor', 'Black')}
								className={`text-[16px] font-semibold py-[12px] px-2 rounded-[5px] w-full cursor-pointer flex items-center justify-center text-center ${door.hardwareColor === 'Black'
									? 'bg-[#699357] text-white hover:bg-[#547545]'
									: 'bg-[#F5F1ED] hover:bg-[#e1dcd7]'
									}`}
							>
								Black
							</div>
							<div
								onClick={() => selectDoorOption(doorId, 'hardwareColor', 'Silver')}
								className={`text-[16px] font-semibold py-[12px] px-2 rounded-[5px] w-full cursor-pointer flex items-center justify-center text-center ${door.hardwareColor === 'Silver'
									? 'bg-[#699357] text-white hover:bg-[#547545]'
									: 'bg-[#F5F1ED] hover:bg-[#e1dcd7]'
									}`}
							>
								Silver
							</div>
						</div>
					</div>
				</>
			)}
		</div>
	);
}

export default DoorConfigurator;
